export interface ApplicationState {
  readonly timeline: TimelineState;
  readonly medias: MediaState;
  readonly addressing: AddressingState;
}

export interface TimelineState {
  parentMediaId: number;

  readonly startDate: Date;
  readonly endDate: Date;
  readonly viewModeType: ViewModeType;
  readonly preset: PresetType;
  readonly selectedMediaId: number;
  readonly sidePanelPosition: SlidePanelPosition;
  readonly sidePanelContent: SlidePanelContent;
  readonly sidePanelSide: SlidePanelSide;
}

export interface MediaState {
  readonly medias: NormalizedMedias;
  readonly tree: TreeItem[];
  readonly lastToggled?: ToggleEvent;

  readonly isLoading: boolean;
  readonly loadingError: string | undefined;
}

export interface AddressingState {
  readonly sites: NormalizedAddressingEntity;
  readonly groups: NormalizedAddressingEntity;
  readonly players: NormalizedAddressingEntity;
  readonly streams: NormalizedAddressingEntity;

  readonly addressingRules: NormalizedAddressingRule;
  readonly selectedGroup: number;
  readonly selectedSite: number;
  readonly selectedPlayer: number;
}

export interface NormalizedAddressingRule {
  [playerId: number]: PlayerAddressingRule;
}

export interface ExtendedAddressingRule {
  [playerId: number]: ExtendedPlayerAddressingRule;
}

export interface NormalizedAddressingEntity {
  [id: number]: AddressingEntity;
}

export enum AddressingEntityType {
  Group = 3,
  Site = 4,
  Player = 5,
  Stream = 6
}

export interface AddressingEntity {
  type: AddressingEntityType;
  name: string;
  id: number;
  children: number[];
  allowMediaId: number[];
  denyMediaId: number[];
  hide?: boolean;
}

export interface IAdvancedPreview {
  open(mediaId: number, rootNode: string): void;
  close?(): void;
}

export interface TreeItem {
  /**
   * Id in the tree. This is unique and has the form of branch address: 1/1/2/1/3/...
   */
  treeId: string;
  /**
   * Id of the parent in the tree. This is unique and has the form of branch address: 1/1/2/1/3/...
   */
  treeParentId: string;
  /**
   * Id of the media. This is not unique in a tree since a media can be used many times in the tree
   */
  id: number;
  /**
   * Id of the parent media. This is not unique in a tree since a media can be used many times in the tree
   */
  mediaParentId: number;
  /**
   * Level in the tree. Top nodes have level 1
   */
  level: number;
  isExpanded: boolean;
  hasChildren: boolean;
  /**
   * The start of the intersection of all the parents date ranges
   */
  startDate: Date | null;
  /**
   * The end of the intersection of all the parents date ranges
   */
  endDate: Date | null;
  /**
   * The start of the intersection of all the parents time ranges - these are computed without the date component which will be set to 01/01/01
   */
  startTime: Date | null;
  /**
   * The end of the intersection of all the parents time ranges - these are computed without the date component which will be set to 01/01/01
   */
  endTime: Date | null;
  /**
   * The intersection of all the parents diffusion days
   */
  diffusionDays: number;
  /**
   * Order in the parent
   */
  porder: number;
  uniqueID: string;
  uniquePARENTID: string;
}

export interface MediaItem {
  id: number;
  name: string;
  mediaType: number;
  startDate: Date;
  endDate: Date;
  startTime: Date;
  endTime: Date;
  diffusionDays: number;
  deniedSites: number[];
  contentFileId: number;
}

export interface NormalizedMedias {
  [id: number]: MediaItem;
}

export const enum ActionTypes {
  FETCH_MEDIAS_REQUEST = 'FETCH_MEDIAS_REQUEST',
  FETCH_MEDIAS_SUCCESS = 'FETCH_MEDIAS_SUCCESS',
  FETCH_MEDIAS_ERROR = 'FETCH_MEDIAS_ERROR',
  FETCH_GROUPS_AND_SITES_REQUEST = 'FETCH_GROUPS_AND_SITES_REQUEST',
  FETCH_GROUPS_AND_SITES_SUCCESS = 'FETCH_GROUPS_AND_SITES_SUCCESS',
  FETCH_GROUPS_AND_SITES_ERROR = 'FETCH_GROUPS_AND_SITES_ERROR',
  EXPAND = 'EXPAND',
  COLLAPSE = 'COLLAPSE',
  SELECT_GROUP = 'SELECT_GROUP',
  SELECT_SITE = 'SELECT_SITE',
  SELECT_PLAYER = 'SELECT_PLAYER',
  RESTORE_FILTERS_SELECTED_VALUES = 'RESTORE_FILTERS_SELECTED_VALUES',
  CLEAR_FILTERS_SELECTED_VALUES = 'CLEAR_FILTERS_SELECTED_VALUES',
  SET_RANGE = 'SET_RANGE',
  SET_VIEW_MODE = 'SET_VIEW_MODE',
  SET_RANGE_AND_VIEW_MODE = 'SET_RANGE_AND_VIEW_MODE',
  SET_PRESET = 'SET_PRESET',
  SET_PARENT_MEDIA_ID = 'SET_PARENT_MEDIA_ID',
  SET_SELECTED_MEDIA_ID = 'SET_SELECTED_MEDIA_ID',
  SET_SLIDE_PANEL_POSITION = 'SET_SLIDE_PANEL_POSITION',
  SET_SLIDE_PANEL_POSITION_AND_CONTENT = 'SET_SLIDE_PANEL_POSITION_AND_CONTENT'
}

export enum TAction {
  Collapse,
  Expand
}

export interface ToggleEvent {
  treeId: string;
  action: TAction;
}

// export interface Item {
//   label: string;
//   value: number;
// }

export interface AddressingDataItem {
  id: number;
  name: string;
  entityType: AddressingEntityType;
  val: string;
  denyMediaId: string;
  allowMediaId: string;
  allLoc: boolean;
}

export interface ExtendedPlayerAddressingRule {
  entityId: number;
  entityType: number;
  denies: AddressingRule[];
  allows: AddressingRule[];
}

export interface PlayerAddressingRule {
  entityId: number;
  entityType: number;
  denies: number[];
}

export interface AddressingRule {
  mediaId: number;
  level: AddressingRuleLevel;
}

export enum AddressingRuleLevel {
  Group = 1,
  Site = 2,
  Player = 3,
  Stream = 4
}

export interface IViewModeColumn {
  startDate: Date;
  endDate: Date;
  title: string;
  subtitle: string;
  includesNow: boolean;
  nowPosition: number;
  headerTitleClassModifier?: string;
  headerSubtitleClassModifier?: string;
  headerCellClassModifier?: string;
  contentCellClassModifier?: string;
  width: number;
}

export interface IViewMode {
  columns: IViewModeColumn[];
  type: ViewModeType;
  computeColumns: (startDate: Date, endDate: Date) => void;
  applyToMedia: (media: MediaItem, treeItem: TreeItem) => TimelineDataItem[];
}

export enum PresetType {
  Week = 'Week',
  Day = 'Day',
  Month = 'Month',
  Year = 'Year',
  Custom = 'Custom'
}

export enum ViewModeType {
  Hours = 'Hours',
  Days = 'Days',
  Weeks = 'Weeks',
  Months = 'Months',
  None = 'None'
}

export enum SlidePanelPosition {
  Closed,
  Opened,
  Docked
}

export enum SlidePanelSide {
  Left,
  Right
}
export enum SlidePanelContent {
  Empty,
  MediaInfo,
  Filters
}

export enum Day {
  None = 0x00,
  Monday = 0x40,
  Tuesday = 0x20,
  Wednesday = 0x10,
  Thursday = 0x08,
  Friday = 0x04,
  Saturday = 0x02,
  Sunday = 0x01
}

export interface TimelineDataItem {
  colIndex: number;
  mediaType: number;
  durations: IMediaItemDuration[];
}

export interface IMediaItemDuration {
  start: number;
  end: number;
  insideValidRange: boolean;
}

export interface DateRangeNavigatorFunctions {
  previous: (startDate: Date, endDate: Date) => { startDate: Date; endDate: Date };
  next: (startDate: Date, endDate: Date) => { startDate: Date; endDate: Date };
  info: (startDate: Date, endDate: Date) => string;
}

export const EVENTS_NODE_NAME = 'Channel events';
export const LOOP_NODE_NAME = 'Channel content';

export enum MediaType {
  None = 0,
  Video = 1,
  Flash = 2,
  Capture = 3,
  TunerTv = 4,
  Image = 5,
  WebPage = 6,
  Scroller = 7,
  Text = 8,
  PowerPoint = 9,
  Audio = 10,
  Rss = 11,
  WmpPlayer = 12,
  Playlist = 13,
  Channel = 14,
  DesignTemplate = 15,
  Tag = 16,
  DesignInstance = 17,
  ScreenLayout = 18,
  PlaylistOfZone = 19,
  FlashEditable = 20,
  PlaylistOfFlash = 21,
  DateTime = 22,
  Color = 23,
  Enum = 24,
  Number = 25,
  PlaylistLocalInputParent = 26,
  FlashEditableTemplate = 27,
  TagLocalInputParent = 28,
  TagLocalInputChild = 29,
  PlaylistLocalInputChild = 30,
  TvPassThru = 31,
  EventPlaylist = 32,
  HtmlTemplate = 33,
  HtmlInstance = 34,
  Folder = 35,
  Feed = 36,
  AdvancedLocalInputPlaylistParent = 37,
  AdvancedLocalInputPlaylistChild = 38,
  AdvancedLocalInputTagParent = 39,
  AdvancedLocalInputTagChild = 40,
  NativeView = 41,
  MusicPlaylist = 42,
  MusicMix = 43,
  MusicProgram = 44,
  FirstValidPlaylist = 45,
  Silence = 46,
  PlayerStream = 47
}



// WEBPACK FOOTER //
// ./src/store/types.ts